import React from 'react'
import { connect, useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router'

import FirebaseControlQuery, {
  getCurrentSessionKey,
} from '@src/firebase-util/firebase-control-query'
import initFirebaseSnapshot from '@src/firebase-util/firebase-snapshot'
import { SessionMap } from '@src/firebase-util/types'
import Routes from '@src/routes'

import { setRoom } from '@store/actionSlices/appConfig'
import {
  Channels,
  setFirebaseLightMap,
  setFirebaseSession,
} from '@store/actionSlices/firestore'
import { setLoadingIndicatorVisibility } from '@store/actionSlices/loadingIndicator'
import { removeFilter as removeLotFilter } from '@store/actionSlices/lotFilter'
import { setProjectIdentity } from '@store/actionSlices/projectIdentity'
import { removeShortlist } from '@store/actionSlices/shortlist'
import { TokenPayloadInterface, setToken } from '@store/actionSlices/token'
import { removeFilter as removeUnitFilter } from '@store/actionSlices/unitFilter'
import { DEFAULT_SHOWCASE_ROOM } from '@store/constants'
import type {
  MasterSessionKey,
  MenuItemInterface,
  ProjectIdentity,
  ProjectSwitch,
  RootStateTypeExtra,
  SessionKey,
} from '@store/types'

import ConnectionRefused from '@pages/errors'

import { useRenewUserTokenMutation } from '@api/authentication'
import {
  EnvisionVRConfigurationInterface,
  SnaploaderConfigurationInterface,
  ThemeConfigInterface,
} from '@api/config'
import {
  MasterKeyData,
  selectFromResult,
  useGetMasterKeyQuery,
} from '@api/master-key'

import useConnectionIndicator from '@utilities/connection-indicator'
import useDetectDisconnect from '@utilities/disconnect-detection'
import {
  getQueryStringParams,
  hasToken,
  initializeSentry,
} from '@utilities/helper'
import LocalStorageCleanerUtil from '@utilities/local-storage-cleaner-util'
import RefreshHandler from '@utilities/refresh-handler'
import ResetUtil from '@utilities/reset-util'
import SSO from '@utilities/sso'
import styleUtil from '@utilities/style-util'
import useDefaultRoom from '@utilities/use-default-room'

import { notifySuccess } from '@adUtilities/notifier'

interface AppProps {
  session: SessionMap | undefined
  projectIdentity: ProjectIdentity
  token: TokenPayloadInterface
  isOnline: boolean
  theme: ThemeConfigInterface
  showcaseRefreshTriggerKey: string
  navigationSettings: MenuItemInterface[]
  envisionVRConfiguration: EnvisionVRConfigurationInterface
  snaploaderConfiguration: SnaploaderConfigurationInterface
  showcaseRoom?: string
}

const { REACT_APP_DATA_POLLING_INTERVAL_X_SECONDS } = process.env

initializeSentry()

function App({
  session,
  projectIdentity,
  token,
  isOnline,
  theme,
  showcaseRefreshTriggerKey,
  navigationSettings,
  envisionVRConfiguration,
  snaploaderConfiguration,
  showcaseRoom,
}: AppProps) {
  styleUtil(theme)

  const location = useLocation()
  const history = useHistory()
  const dispatch = useDispatch()
  const resetUtil = ResetUtil()

  const firebaseSession = initFirebaseSnapshot(
    'devsuite',
    projectIdentity.projectName,
    'session',
    getCurrentSessionKey({
      projectName: projectIdentity.projectName,
      sessionKeys: projectIdentity.sessionId,
    })
  )

  const firebaseLightMap = initFirebaseSnapshot(
    'devsuite',
    projectIdentity.projectName,
    'light-map',
    projectIdentity.lightMap
  )

  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKeys: projectIdentity.sessionId,
  })

  React.useEffect(() => {
    if (showcaseRoom?.toLowerCase() === DEFAULT_SHOWCASE_ROOM) {
      dispatch(setRoom(''))
    }
  }, [])

  React.useEffect(() => {
    if (firebaseSession) {
      dispatch(setFirebaseSession(firebaseSession as SessionMap))
    }
  }, [firebaseSession])

  React.useEffect(() => {
    if (firebaseLightMap) {
      dispatch(setFirebaseLightMap(firebaseLightMap as Channels))
    }
  }, [firebaseLightMap])

  const { hasDisconnected } = useDetectDisconnect(session as SessionMap)

  const [isIframe, setIsIframe] = React.useState(false)
  const [sessionKeys, setSessionKeys] = React.useState<Array<string>>([])
  const [tokenRenewalIntervalId, setTokenRenewalIntervalId] =
    React.useState<ReturnType<typeof setInterval>>()
  const [tokenRenewalState, setTokenRenewalState] = React.useState(false)
  const [userId, setUserId] = React.useState('')
  const [skipMasterKeyApi, setSkipMasterKeyApi] = React.useState(true)

  const masterKeyPayload = useGetMasterKeyQuery(
    {
      masterKey: projectIdentity.masterKey,
      userId,
    },
    {
      selectFromResult,
      skip: skipMasterKeyApi,
      pollingInterval:
        Number(REACT_APP_DATA_POLLING_INTERVAL_X_SECONDS || 10) * 1000,
    }
  )

  const [
    renewUserToken,
    {
      isLoading: refreshTokenIsLoading,
      data: refreshTokenData,
      error: refreshTokenError,
    },
  ] = useRenewUserTokenMutation()

  const {
    connected,
    sessionOwners: firebaseSessionOwners,
    projectSwitch,
  } = session || {}

  const { lightCode, access_token = '' } = getQueryStringParams(location.search)

  useConnectionIndicator({ token, isOnline })

  LocalStorageCleanerUtil({ token })

  RefreshHandler({
    refreshTriggerKeyFirebase: session?.showcaseRefreshTriggerKey ?? '',
    refreshTriggerKeyLocal: showcaseRefreshTriggerKey,
  })

  SSO({ accessToken: access_token })

  const handleTokenRenewalProcess = () => {
    if (!hasToken(token)) {
      return
    }

    const {
      access_token: { expires_at: expiresAt },
      refresh_token: { token: refreshToken },
      email,
    } = token

    const currentTime = new Date().getTime()
    const tokenExpiryTime = new Date(expiresAt).getTime()
    const timeDifference = tokenExpiryTime - currentTime
    const timeDifferenceInDays = timeDifference / (1000 * 60 * 60 * 24)
    if (timeDifferenceInDays <= 7) {
      renewUserToken({ refresh_token: refreshToken, email })
    }
  }

  const resetTokenRenewalInterval = () => {
    if (tokenRenewalIntervalId) {
      clearInterval(tokenRenewalIntervalId)
      setTokenRenewalIntervalId(undefined)
    }
  }

  const setTokenRenewalInterval = () => {
    if (!hasToken(token)) {
      resetTokenRenewalInterval()
      return
    }

    if (!tokenRenewalIntervalId) {
      setTokenRenewalIntervalId(
        setInterval(() => setTokenRenewalState(true), 1000 * 86400)
      )
    }
  }

  const handleAppId = (appIdleKey: string) => {
    if (appIdleKey) {
      firebaseControlQuery.update({
        [`appIdleKey`]: '',
      })
    }
  }

  const handleClientCheck = (client: boolean) => {
    if (!client) {
      firebaseControlQuery.update({
        [`client.showcase`]: true,
      })
    }
  }

  const handleMasterSessionKeys = React.useCallback(
    (argMasterKeyData: MasterKeyData) => {
      const { sessionKeys: masterSessionKeys } = argMasterKeyData

      if (masterSessionKeys.length < 1) {
        return
      }

      const sessionId: Array<SessionKey> = masterSessionKeys.map(
        (item: MasterSessionKey) => ({
          key: item.key,
          project: item.projectId,
        })
      )

      dispatch(setProjectIdentity({ ...projectIdentity, sessionId }))
    },
    [masterKeyPayload]
  )

  React.useEffect(() => {
    if (!sessionKeys) {
      return
    }
    if (!connected) {
      setSessionKeys([])
      return
    }

    if (firebaseSessionOwners) {
      firebaseSessionOwners.forEach((res) => {
        if (!sessionKeys.find((ses: string) => res.name === ses)) {
          if (res.name && res.email && sessionKeys) {
            notifySuccess(`${res.name} is Connected`)
            setSessionKeys([...sessionKeys, res.name])
          }
        }
      })
    }
  }, [firebaseSessionOwners])

  const { getDefaultRoom, setDefaultRoom } = useDefaultRoom()

  const handleRemoteProjectSwitch = async (
    { projectName, projectId, projectLabel }: ProjectSwitch,
    activeRoute?: string
  ) => {
    dispatch(
      setProjectIdentity({
        ...projectIdentity,
        projectName,
        projectId,
        projectLabel,
      })
    )
    const defaultRoom = await getDefaultRoom({
      projectName,
      prioritizeCurrentRoom: true,
    })

    if (defaultRoom) {
      setDefaultRoom(defaultRoom)
    }

    resetUtil.resetLocalStorage()

    await firebaseControlQuery.update({
      [`projectSwitch`]: {
        projectId: '',
        projectLabel: '',
        projectName: '',
      },
    })

    history.replace({ pathname: `/${projectName}/${activeRoute}` })
    window.location.reload()
  }

  React.useEffect(() => {
    if (lightCode) {
      dispatch(setProjectIdentity({ lightMap: lightCode }))
    }
  }, [lightCode])

  React.useEffect(() => {
    const { sessionId } = projectIdentity
    if (!projectSwitch) {
      return
    }

    const { projectName, projectLabel, projectId } = projectSwitch

    if (!sessionId.find((res) => res.project === projectName)) {
      return
    }

    if (projectName && projectLabel && projectId) {
      handleRemoteProjectSwitch(projectSwitch, session?.activeRoute)
    }
  }, [projectSwitch])

  React.useEffect(() => {
    dispatch(setLoadingIndicatorVisibility(refreshTokenIsLoading))
  }, [refreshTokenIsLoading])

  React.useEffect(() => {
    if (refreshTokenData && refreshTokenData.data) {
      const {
        data: { user },
      } = refreshTokenData
      dispatch(setToken(user as TokenPayloadInterface))
    }
  }, [refreshTokenData])

  React.useEffect(() => {
    if (refreshTokenError) {
      console.error('Failed to renew the user token.', refreshTokenError)
    }
  }, [refreshTokenError])

  React.useEffect(() => {
    setTokenRenewalInterval()
  }, [token])

  React.useEffect(() => {
    if (tokenRenewalState) {
      handleTokenRenewalProcess()
      setTokenRenewalState(false)
    }
  }, [tokenRenewalState])

  React.useEffect(() => {
    if (hasToken(token) && projectIdentity.masterKey) {
      const { _id } = token
      setUserId(_id)
      setSkipMasterKeyApi(false)
    } else {
      setSkipMasterKeyApi(true)
    }
  }, [token, projectIdentity])

  React.useEffect(() => {
    const { masterKeyData, isError } = masterKeyPayload
    if (!isError && Object.keys(masterKeyData).length > 0) {
      handleMasterSessionKeys(masterKeyData)
    }
  }, [masterKeyPayload])

  React.useEffect(() => {
    setIsIframe(window.self !== window.top)
  }, [])

  React.useEffect(() => {
    if (session) {
      const { appIdleKey, client } = session

      handleAppId(appIdleKey)
      handleClientCheck(client?.showcase)
    }
  }, [session])

  React.useEffect(() => {
    if (hasDisconnected) {
      localStorage.removeItem('shortlist')
      localStorage.removeItem('unitFilter')
      localStorage.removeItem('lotFilter')
      dispatch(removeShortlist())
      dispatch(removeUnitFilter())
      dispatch(removeLotFilter())
      if (location.pathname !== '/vision') {
        history.push('vision')
      }
    }
  }, [hasDisconnected])

  if (isIframe) {
    return <ConnectionRefused />
  }

  return (
    <Routes
      {...{
        session: session as SessionMap,
        projectIdentity,
        token,
        navigationSettings,
        envisionVRConfiguration,
        snaploaderConfiguration,
      }}
    />
  )
}

export default connect(
  ({
    projectIdentity,
    token,
    connectionIndicator: { isOnline },
    projectConfig: {
      theme,
      showcaseRefreshTriggerKey,
      navigationSettings,
      envisionVRConfiguration,
      snaploaderConfiguration,
    },
    firestore: { session },
    appConfig: { room: showcaseRoom },
  }: RootStateTypeExtra) => ({
    session,
    projectIdentity,
    token,
    isOnline,
    theme,
    showcaseRefreshTriggerKey,
    navigationSettings,
    envisionVRConfiguration,
    snaploaderConfiguration,
    showcaseRoom,
  })
)(App)
